import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MeterRole, METER_ROLE_ICONS } from '@core/models';
import { CardComponent } from '@custom-components/general/card/card.component';
import { FilledButtonComponent } from '@custom-components/general/button';
import { SelectFieldComponent } from '@custom-components/general/form/select-field/select-field.component';
import {
  ConfirmWarningDialogComponent,
  ConfirmWarningDialogData,
} from '@custom-components/overlay/confirm-warning-dialog/confirm-warning-dialog.component';
import { DEFAULT_METER_ROLE_EDITOR_ROLES } from '../../constants/default-meter-role-editor-roles.constant';

const METER_ROLE_OPTIONS: MeterRole[] = ['consumption', 'production', 'bidirectional'];

@Component({
  selector: 'xemex-my-company-default-meter-role',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatIconModule,
    CardComponent,
    FilledButtonComponent,
    SelectFieldComponent,
  ],
  templateUrl: './my-company-default-meter-role.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyCompanyDefaultMeterRoleComponent implements OnChanges {
  defaultMeterRole = input<MeterRole | undefined>();
  isLoading = input<boolean>(false);
  isSaving = input<boolean>(false);
  userRole = input<string | undefined>();

  updateDefaultMeterRole = output<MeterRole>();

  protected readonly meterRoleOptions = METER_ROLE_OPTIONS;
  protected readonly meterRoleIcons = METER_ROLE_ICONS;
  protected readonly canEdit = computed(() => {
    const roleName = this.userRole();
    return !!roleName && DEFAULT_METER_ROLE_EDITOR_ROLES.includes(roleName);
  });

  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);

  protected readonly form = new FormGroup({
    defaultMeterRole: new FormControl<MeterRole>('bidirectional'),
  });

  private savedValue: MeterRole | undefined = undefined;

  ngOnChanges(): void {
    const value = this.defaultMeterRole();
    if (!value || value === this.savedValue) return;

    // Only re-seed the select when the user hasn't started an unsaved edit — otherwise a
    // late-arriving fetch (or the optimistic update right after save) would silently
    // overwrite the in-progress choice. `savedValue` still tracks the latest persisted
    // truth either way, so isDirty()/onSave() keep comparing against the right baseline.
    // On the very first value (savedValue still undefined), the form's hardcoded initial
    // control value must not count as a user edit, or the select would never seed at all.
    const isFirstLoad = this.savedValue === undefined;
    const wasDirty = !isFirstLoad && this.isDirty();
    this.savedValue = value;
    if (!wasDirty) {
      this.form.controls.defaultMeterRole.setValue(value);
    }
  }

  /** Not a computed signal — the form control's value isn't itself a signal, so this is
   * re-evaluated on every change-detection pass (triggered by the mat-select's own DOM event,
   * same as any other OnPush component reacting to a template-bound event). */
  protected isDirty(): boolean {
    return this.form.controls.defaultMeterRole.value !== this.savedValue;
  }

  protected onSave(): void {
    const value = this.form.controls.defaultMeterRole.value;
    if (!value || value === this.savedValue) return;

    const data: ConfirmWarningDialogData = {
      title: this.translateService.instant(
        'companies.detailTabs.general.defaultMeterRole.confirmTitle',
      ),
      message: this.translateService.instant(
        'companies.detailTabs.general.defaultMeterRole.confirmMessage',
      ),
      confirmLabel: this.translateService.instant(
        'companies.detailTabs.general.defaultMeterRole.confirmButton',
      ),
      cancelLabel: this.translateService.instant(
        'companies.detailTabs.general.defaultMeterRole.cancelButton',
      ),
    };

    this.dialog
      .open(ConfirmWarningDialogComponent, { data })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed === true) {
          this.updateDefaultMeterRole.emit(value);
        }
      });
  }
}
